import { normalizeTag } from '@nedelnik/shared';

import type { Task } from '@nedelnik/shared';

/**
 * Теги (3.10).
 *
 * Служат двум целям сразу — группировке по смыслу и связи задач между собой
 * (3.10.1), — поэтому считаются по всем задачам, а не по показанному спринту:
 * «сколько всего висит по дому» это вопрос про дом, а не про неделю.
 */

export interface TagCount {
  tag: string;
  /** Незакрытых задач с этим тегом. Закрытые в счётчике не нужны: они ничего не требуют. */
  open: number;
}

/**
 * Список всех тегов с числом незакрытых (3.10.2).
 *
 * Сверху то, где больше висит; при равенстве — по алфавиту, чтобы порядок не
 * прыгал от каждой закрытой задачи. Тег, у которого все задачи закрыты, из
 * списка не исчезает: по нему всё ещё осмысленно отфильтровать.
 */
export function tagCounts(
  tasks: readonly Task[],
  tagsByTask: ReadonlyMap<string, string[]>,
): TagCount[] {
  const counts = new Map<string, number>();

  for (const task of tasks) {
    for (const tag of tagsByTask.get(task.id) ?? []) {
      const open = counts.get(tag) ?? 0;
      counts.set(tag, open + (task.status === 'done' ? 0 : 1));
    }
  }

  return [...counts]
    .map(([tag, open]) => ({ tag, open }))
    .sort((a, b) => (a.open === b.open ? a.tag.localeCompare(b.tag) : b.open - a.open));
}

/**
 * Какие из существующих тегов предложить в карточке (3.10.5, 3.10.6).
 *
 * Подсказка здесь не ради экономии набора, а против двойников: «дом» рядом с
 * «домашнее» замечают, когда оба уже стоят на десятке задач и слить их нечем.
 * Поэтому существующие теги показываются до того, как заведён новый.
 *
 * Пока поле пустое, предлагаются самые ходовые — в порядке `tagCounts`, где
 * больше незакрытых, тот выше. Набранное сужает список до тегов, в чьё
 * название оно входит; начинающиеся с набранного идут первыми: «до» скорее
 * про «дом», чем про «сдоба». Сравнение — в нормализованном виде, как и у
 * самого навешивания: «Дом» обязан найти «дом».
 *
 * Уже стоящие на задаче не предлагаются вовсе (3.10.6): показать тег, который
 * действие тут же отвергнет как дубль, — то же, что в подборщике задач
 * предложить уже связанную.
 */
export function suggestTags(
  all: readonly TagCount[],
  own: readonly string[],
  query: string,
  limit = 8,
): string[] {
  const taken = new Set(own.map(normalizeTag));
  const needle = normalizeTag(query.replace(/^#/, ''));

  const free = all.map(({ tag }) => tag).filter((tag) => !taken.has(normalizeTag(tag)));
  if (needle.length === 0) return free.slice(0, limit);

  // Ранг вхождения: с начала слова — 0, изнутри — 1, нет вовсе — не подходит.
  const rank = (tag: string): number => {
    const at = normalizeTag(tag).indexOf(needle);
    return at < 0 ? -1 : at === 0 ? 0 : 1;
  };

  return (
    free
      .filter((tag) => rank(tag) >= 0)
      // Сортировка устойчивая: внутри одного ранга порядок остаётся по ходовости.
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, limit)
  );
}
